import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const REFRESH_MS = 5 * 60 * 1000;

export default function useUpcomingCalendarEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [feedUrls, setFeedUrls] = useState([]);
  const abortRef = useRef(null);

  const loadEvents = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError('');
    try {
      const callable = httpsCallable(functions, 'getTeamScheduleEvents');
      const { data } = await callable({ weeksAhead: 8, max: 150 });
      const rows = Array.isArray(data?.events) ? data.events : [];
      setEvents(rows);
      setFeedUrls(Array.isArray(data?.sourceFeeds) ? data.sourceFeeds : []);
      setLastLoadedAt(new Date());
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Failed to load calendar events:', err);
      setError('Could not load schedule highlights right now.');
      setEvents([]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    const timer = window.setInterval(loadEvents, REFRESH_MS);
    return () => {
      window.clearInterval(timer);
      abortRef.current?.abort();
    };
  }, [loadEvents]);

  return {
    events,
    loading,
    error,
    refresh: loadEvents,
    lastLoadedAt,
    feedUrls,
  };
}
